/**
 * Liste normalisée des unités de mesure pour ingrédients et recettes.
 * Utilisée dans les formulaires ingrédients et produits.
 */
export const MEASUREMENT_UNITS = [
  // Masse
  { value: "kg", label: "kg (kilogramme)" },
  { value: "g", label: "g (gramme)" },
  { value: "mg", label: "mg (milligramme)" },
  // Volume
  { value: "L", label: "L (litre)" },
  { value: "mL", label: "mL (millilitre)" },
  { value: "cL", label: "cL (centilitre)" },
  { value: "dL", label: "dL (décilitre)" },
  // Comptage / pièces
  { value: "pièce", label: "pièce" },
  { value: "unité", label: "unité" },
  { value: "portion", label: "portion" },
  { value: "tranche", label: "tranche" },
  { value: "gousse", label: "gousse" },
  { value: "tête", label: "tête" },
  { value: "botte", label: "botte" },
  { value: "branche", label: "branche" },
  // Cuillères
  { value: "c. à s.", label: "c. à s. (cuillère à soupe)" },
  { value: "c. à c.", label: "c. à c. (cuillère à café)" },
  // Autres
  { value: "pincée", label: "pincée" },
  { value: "noix", label: "noix (beurre)" },
  { value: "sachet", label: "sachet" },
  { value: "boîte", label: "boîte" },
  { value: "pot", label: "pot" },
  { value: "tasse", label: "tasse" },
  { value: "verre", label: "verre" },
] as const;

export type MeasurementUnitValue = (typeof MEASUREMENT_UNITS)[number]["value"];

/** Valeurs des unités pour validation / Select */
export const MEASUREMENT_UNIT_VALUES = MEASUREMENT_UNITS.map((u) => u.value);

/** Retourne le libellé complet d'une unité ou la valeur brute si non trouvée */
export function getUnitLabel(value: string | null | undefined): string {
  if (!value) return "—";
  const found = MEASUREMENT_UNITS.find((u) => u.value === value);
  return found ? found.label : value;
}

/** Retourne l'unité courte pour affichage compact (ex: tableau, total) */
export function getUnitShort(value: string | null | undefined): string {
  if (!value) return "u.";
  return value;
}

/** Seuils de conversion pour affichage lisible (ex: 4550 g -> 4,55 kg) */
// Ordre important : conversions vers unités plus grandes en premier (ex: mg->kg avant mg->g)
const CONVERSION_RULES: Array<{ from: string; to: string; threshold: number; factor: number }> = [
  { from: "mg", to: "kg", threshold: 1_000_000, factor: 1_000_000 },
  { from: "mg", to: "g", threshold: 1000, factor: 1000 },
  { from: "g", to: "kg", threshold: 1000, factor: 1000 },
  { from: "mL", to: "L", threshold: 1000, factor: 1000 },
  { from: "cL", to: "L", threshold: 100, factor: 100 },
  { from: "dL", to: "L", threshold: 10, factor: 10 },
];

/**
 * Formate une quantité avec conversion automatique si trop grande.
 * Ex: 4550 g -> "4,55 kg", 1500 mL -> "1,5 L"
 */
export function formatQuantityWithConversion(
  quantity: number,
  unit: string | null | undefined
): { value: string; unit: string } {
  const u = unit?.trim() || "u.";
  const q = Number(quantity);
  if (!Number.isFinite(q) || q < 0) {
    return { value: "0", unit: getUnitShort(unit) };
  }

  // Appliquer les règles de conversion (ordre : plus grande unité d'abord pour mg->kg)
  const rules = CONVERSION_RULES.filter((r) => r.from === u);
  for (const rule of rules) {
    if (q >= rule.threshold) {
      const converted = q / rule.factor;
      return {
        value: converted.toLocaleString("fr-FR", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }),
        unit: rule.to,
      };
    }
  }

  return {
    value: q.toLocaleString("fr-FR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }),
    unit: u,
  };
}
